import { clsx } from 'clsx'
import {
  GENDER_OPTIONS,
  ORIENTATION_OPTIONS,
  feedPrefsReady,
  formatSexualitySummary,
} from '../lib/feedPrefs'
import type { FeedPrefs } from '../types'

/** Step 1 before every round — gender + sexuality with symbols. */
export function SexualityPicker({
  prefs,
  onGender,
  onOrientation,
  step = 1,
}: {
  prefs: FeedPrefs
  onGender: (g: NonNullable<FeedPrefs['gender']>) => void
  onOrientation: (o: NonNullable<FeedPrefs['orientation']>) => void
  step?: number
}) {
  const ready = feedPrefsReady(prefs)
  const summary = formatSexualitySummary(prefs)

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[3px] text-kapow">
        <span className="inline-block h-px w-6 bg-kapow/50" />
        {step}. SEXUALITY — SET BEFORE EACH ROUND
        <span className="inline-block h-px w-6 bg-kapow/50" />
      </div>
      <div className="cyber-panel cyber-cyan p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-center text-[10px] font-bold uppercase tracking-[2px] text-muted">
              You are
            </p>
            <div className="flex justify-center gap-2">
              {GENDER_OPTIONS.map((opt) => {
                const on = prefs.gender === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onGender(opt.value)}
                    className={clsx(
                      'flex min-w-[4.5rem] flex-col items-center gap-0.5 rounded-lg border-2 px-3 py-2 transition active:scale-[0.97]',
                      on
                        ? 'border-kapow bg-kapow/15 text-white shadow-neon-cyan'
                        : 'border-edge bg-[#0a0a10] text-muted hover:border-kapow/50 hover:text-white',
                    )}
                    aria-pressed={on}
                    title={opt.label}
                  >
                    <span className="text-2xl leading-none">{opt.symbol}</span>
                    <span className="text-[9px] font-bold uppercase tracking-widest">{opt.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <p className="mb-2 text-center text-[10px] font-bold uppercase tracking-[2px] text-muted">
              Sexuality
            </p>
            <div className="flex justify-center gap-2">
              {ORIENTATION_OPTIONS.map((opt) => {
                const on = prefs.orientation === opt.value
                const symbol =
                  opt.value === 'gay' && prefs.gender === 'female' ? '⚢' : opt.symbol
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onOrientation(opt.value)}
                    className={clsx(
                      'flex min-w-[4.5rem] flex-col items-center gap-0.5 rounded-lg border-2 px-3 py-2 transition active:scale-[0.97]',
                      on
                        ? 'border-pow bg-pow/15 text-white shadow-neon-pink'
                        : 'border-edge bg-[#0a0a10] text-muted hover:border-pow/50 hover:text-white',
                    )}
                    aria-pressed={on}
                    title={opt.label}
                  >
                    <span className="text-2xl leading-none">{symbol}</span>
                    <span className="text-[9px] font-bold uppercase tracking-widest">{opt.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {ready && summary ? (
          <p className="mt-3 text-center font-comic text-sm tracking-wide text-pow">
            {summary} locked in for this round
          </p>
        ) : (
          <p className="mt-3 text-center text-xs text-muted">
            Pick gender and sexuality before choosing tags
          </p>
        )}
      </div>
    </div>
  )
}